import type { ExtractionCheck, ExtractionJob } from '../../api/aiExtraction';
import type { OcrData } from './OcrReviewForm';

type CardFields = Record<string, unknown> | undefined;
const s = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined);

const fold = (v: string) =>
  v
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/gi, 'd')
    .toUpperCase()
    .replace(/\s+/g, ' ')
    .trim();

/**
 * One OCR form from the two sides of a CCCD. The back's MRZ, when its check
 * digits passed, has proved the number, birth date and gender digit for digit,
 * so it wins for those; the front is the only place the name with diacritics
 * and the address are printed; the issue date is on the back.
 */
export function mergeCardReads(front: CardFields, back: CardFields): Partial<OcrData> {
  const backTrusted = back?.mrzVerified === true;
  const pick = (...vals: unknown[]) => vals.map(s).find(Boolean);
  return {
    hoTen: pick(front?.fullName, back?.fullName),
    soCccd: pick(backTrusted ? back?.cccd : undefined, front?.cccd, back?.cccd),
    ngaySinh: pick(backTrusted ? back?.dob : undefined, front?.dob, back?.dob),
    gioiTinh: pick(backTrusted ? back?.gender : undefined, front?.gender, back?.gender),
    diaChi: pick(front?.address, back?.address),
    ngayCap: pick(back?.issueDate, front?.issueDate),
  };
}

/** The backend checks each photo on its own; this adds the checks that need
 * both sides, and drops duplicates. */
export function combineCardChecks(front: ExtractionJob | undefined, back: ExtractionJob | undefined): ExtractionCheck[] {
  const seen = new Set<string>();
  const out: ExtractionCheck[] = [];
  for (const c of [...(front?.checks ?? []), ...(back?.checks ?? [])]) {
    if (c.code === 'MOCK' || seen.has(c.label)) continue;
    seen.add(c.label);
    out.push(c);
  }
  const frontId = s(front?.fields?.cccd);
  const backId = back?.fields?.mrzVerified === true ? s(back.fields.cccd) : undefined;
  if (frontId && backId) {
    out.push(
      frontId === backId
        ? { code: 'FRONT_BACK_ID', ok: true, label: 'Số CCCD mặt trước khớp mã MRZ mặt sau' }
        : { code: 'FRONT_BACK_ID', ok: false, label: 'Số CCCD mặt trước KHÁC mã MRZ mặt sau - có thể là hai thẻ khác nhau' },
    );
  }
  const frontName = s(front?.fields?.fullName);
  const backName = back?.fields?.mrzVerified === true ? s(back.fields.fullName) : undefined;
  if (frontName && backName) {
    const same = fold(frontName) === fold(backName);
    out.push({ code: 'FRONT_BACK_NAME', ok: same, label: same ? 'Họ tên mặt trước khớp MRZ mặt sau' : 'Họ tên mặt trước khác MRZ mặt sau' });
  }
  return out;
}
